import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, InputAmount, SmartDataTable, Badge, getStatusBadge, Modal } from '@/backoffice/components/ui';
import { formatCurrency, formatDate, formatRelativeTime } from '@/backoffice/utils';
import {
  fetchClientWallet,
  depositWallet,
  withdrawWallet,
  type ClientWalletData,
  type WalletTransaction,
} from '@/domains/client/services/client.service';
import {
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  Clock,
  TrendingUp,
  Plus,
  ArrowRight,
  Loader2,
} from 'lucide-react';

const providers = [
  { id: 'orange' as const, label: 'Orange Money', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { id: 'mtn' as const, label: 'MTN MoMo', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
];

export function WalletPage() {
  const [wallet, setWallet] = useState<ClientWalletData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<'deposit' | 'withdraw' | null>(null);
  const [amount, setAmount] = useState(0);
  const [phone, setPhone] = useState('');
  const [provider, setProvider] = useState<'orange' | 'mtn'>('orange');

  const loadWallet = () => {
    setLoading(true);
    fetchClientWallet()
      .then(setWallet)
      .catch(() => setWallet(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadWallet();
  }, []);

  const openModal = (type: 'deposit' | 'withdraw') => {
    setModal(type);
    setAmount(0);
    setPhone('');
    setProvider('orange');
    setError(null);
  };

  const handleSubmit = async () => {
    if (!amount || amount < 1000 || !phone.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        amount,
        payer_phone: phone.trim(),
        mobile_money_provider: provider,
      };
      const updated = modal === 'deposit'
        ? await depositWallet(payload)
        : await withdrawWallet(payload);
      setWallet(updated);
      setModal(null);
    } catch {
      setError(modal === 'deposit'
        ? 'Échec de la recharge. Vérifiez vos informations.'
        : 'Échec du retrait. Vérifiez votre solde et réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="client" title="Portefeuille">
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement...
        </div>
      </DashboardLayout>
    );
  }

  if (!wallet) {
    return (
      <DashboardLayout userType="client" title="Portefeuille">
        <Card className="text-center py-12">
          <p className="text-gray-500">Impossible de charger le portefeuille.</p>
        </Card>
      </DashboardLayout>
    );
  }

  const pendingWithdrawals = wallet.pending_withdrawals;

  return (
    <DashboardLayout userType="client" title="Portefeuille">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Wallet className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-blue-100">Solde disponible</p>
                <p className="text-3xl font-bold">{formatCurrency(wallet.balance)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-blue-100">En attente</p>
                <p className="text-lg font-semibold">{formatCurrency(wallet.pending_amount)}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-blue-100">Ce mois</p>
                <p className="text-lg font-semibold">+{formatCurrency(wallet.monthly_savings)}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                className="flex-1 bg-white/20 hover:bg-white/30 text-white"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => openModal('deposit')}
              >
                Recharger
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-white/50 text-white hover:bg-white/10"
                leftIcon={<ArrowUpRight className="w-4 h-4" />}
                onClick={() => openModal('withdraw')}
                disabled={wallet.balance < 1000}
              >
                Retirer
              </Button>
            </div>
          </Card>

          {pendingWithdrawals.length > 0 && (
            <Card className="mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-amber-500" />
                <h3 className="font-semibold text-gray-900">En attente</h3>
              </div>
              {pendingWithdrawals.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div>
                    <p className="font-medium text-gray-900">Retrait en cours</p>
                    <p className="text-sm text-gray-600">{t.provider} • {t.reference}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-amber-700">{formatCurrency(t.amount)}</p>
                    <Badge variant="warning" size="sm">En cours</Badge>
                  </div>
                </div>
              ))}
            </Card>
          )}

          <Card className="mt-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-200 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm text-emerald-700">Economies communautaires</p>
                <p className="text-xl font-bold text-emerald-900">{formatCurrency(wallet.monthly_savings)}</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Historique des transactions</h3>
              <p className="text-sm text-gray-500">Recharges, retraits et paiements</p>
            </div>

            <SmartDataTable
              data={wallet.transactions}
              columns={[
                {
                  key: 'created_at',
                  title: 'Date',
                  sortable: true,
                  render: (item: WalletTransaction) => (
                    <div>
                      <p className="font-medium text-gray-900">{formatRelativeTime(item.created_at)}</p>
                      <p className="text-xs text-gray-500">{formatDate(item.created_at, 'short')}</p>
                    </div>
                  ),
                },
                {
                  key: 'type',
                  title: 'Type',
                  render: (item: WalletTransaction) => (
                    <div className="flex items-center gap-2">
                      {item.type === 'withdrawal' ? (
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                          <ArrowUpRight className="w-4 h-4 text-red-600" />
                        </div>
                      ) : item.type === 'order_payment' ? (
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <ArrowRight className="w-4 h-4 text-teal-600" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                          <ArrowDownLeft className="w-4 h-4 text-emerald-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">
                          {item.type === 'withdrawal' ? 'Retrait' :
                           item.type === 'order_payment' ? 'Paiement commande' :
                           item.type === 'deposit' ? 'Recharge' :
                           item.type === 'refund' ? 'Remboursement' : 'Collecte COD'}
                        </p>
                        {item.provider && (
                          <p className="text-xs text-gray-500">{item.provider}</p>
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'amount',
                  title: 'Montant',
                  sortable: true,
                  render: (item: WalletTransaction) => (
                    <p className={`font-semibold ${
                      item.type === 'withdrawal' || item.type === 'order_payment'
                        ? 'text-red-600'
                        : 'text-emerald-600'
                    }`}>
                      {item.type === 'withdrawal' || item.type === 'order_payment' ? '-' : '+'}
                      {formatCurrency(item.amount)}
                    </p>
                  ),
                },
                {
                  key: 'status',
                  title: 'Statut',
                  render: (item: WalletTransaction) => getStatusBadge(item.status),
                },
              ]}
              keyExtractor={(item) => item.id}
              emptyMessage="Aucune transaction"
            />
          </Card>
        </div>
      </div>

      <Modal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        title={modal === 'deposit' ? 'Recharger le portefeuille' : 'Retirer des fonds'}
      >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}
        <div className="space-y-4">
          <InputAmount
            label="Montant (GNF)"
            value={amount}
            onChange={setAmount}
            hint="Minimum 1 000 GNF"
          />
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Opérateur Mobile Money</p>
            <div className="grid grid-cols-2 gap-2">
              {providers.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProvider(p.id)}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    provider === p.id ? `${p.color} border-current` : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <Input
            label="Numéro Mobile Money"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Ex: 620 00 00 01"
          />
          {modal === 'withdraw' && (
            <p className="text-sm text-gray-500">
              Solde disponible : <strong>{formatCurrency(wallet.balance)}</strong>
            </p>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="flex-1" onClick={() => setModal(null)}>
            Annuler
          </Button>
          <Button
            className="flex-1"
            onClick={handleSubmit}
            isLoading={submitting}
            disabled={amount < 1000 || !phone.trim()}
          >
            Confirmer
          </Button>
        </div>
      </Modal>
    </DashboardLayout>
  );
}
